import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Text from '../components/shared/Text';
import { useToast } from '../components/shared/ToastNotification';

interface ProductRow {
    id: number;
    name: string;
    productType: string;
    categoryId: string;
    sku: string;
    hsnCode: string;
    unit: string;
    stockQuantity: string | number;
    tax_rate_id: string | number;
    tax_rate: number | string;
    taxInclusive: boolean;
    sellingPrice: string | number;
    mrp: string | number;
    purchasePrice: string | number;
    serialized: boolean;
    showResults: boolean;
    serialNumbers?: string[];
    batches?: any[];
}

function QuickProductEntry() {
    const navigate = useNavigate();
    const toast = useToast();
    const [rows, setRows] = useState<ProductRow[]>([createEmptyRow(0)]);
    const [categories, setCategories] = useState<any[]>([]);
    const [taxRates, setTaxRates] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchCategories();
        fetchTaxRates();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || []);
        } catch (error) {
            console.error('Error fetching tax rates:', error);
        }
    };

    const [searchCache, setSearchCache] = useState<{ [key: number]: any[] }>({});
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const searchProducts = (query: string, rowIndex: number) => {
        if (!query || query.length < 2) {
            updateRow(rowIndex, { showResults: false });
            return;
        }

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Debounce API call
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                // Use the search endpoint instead of client-side filtering
                const response = await api.get(`/search/products`, { q: query });
                const filtered = response.data?.products || response.products || [];

                setSearchCache(prev => ({ ...prev, [rowIndex]: filtered }));
                if (filtered.length > 0) {
                    updateRow(rowIndex, { showResults: true });
                } else {
                    updateRow(rowIndex, { showResults: false });
                }
            } catch (error) {
                console.error("Search failed:", error);
            }
        }, 300);
    };

    function createEmptyRow(index: number): ProductRow {
        return {
            id: Date.now() + index,
            name: '',
            productType: 'store_sale',
            categoryId: '',
            sku: '',
            hsnCode: '',
            unit: 'piece',
            stockQuantity: '',
            tax_rate_id: '',
            tax_rate: 0,
            taxInclusive: false,
            sellingPrice: '',
            mrp: '',
            purchasePrice: '',
            serialized: false,
            showResults: false,
            batches: []
        };
    }

    const updateRow = (index: number, updates: Partial<ProductRow>) => {
        setRows(prev => prev.map((row, i) =>
            i === index ? { ...row, ...updates } : row
        ));
    };

    const addRow = () => {
        setRows(prev => [...prev, createEmptyRow(prev.length)]);
    };

    const removeRow = (index: number) => {
        if (rows.length === 1) {
            setRows([createEmptyRow(0)]);
        } else {
            setRows(prev => prev.filter((_, i) => i !== index));
        }
    };

    const saveProducts = async () => {
        const validRows = rows.filter(r => r.name.trim() !== '');

        if (validRows.length === 0) {
            toast.warning('Please add at least one product with a name');
            return;
        }

        const productsData = validRows.map(row => ({
            name: row.name,
            product_type: row.productType || 'store_sale',
            category_id: row.categoryId || null,
            sku: row.sku || null,
            hsn_code: row.hsnCode || null,
            unit: row.unit,
            stock_quantity: parseFloat(String(row.stockQuantity)) || 0,
            selling_price: parseFloat(String(row.sellingPrice)) || 0,
            mrp: parseFloat(String(row.mrp)) || 0,
            purchase_price: parseFloat(String(row.purchasePrice)) || 0,
            tax_rate_id: row.tax_rate_id || null,
            tax_inclusive: row.taxInclusive,
            serialized: row.serialized,
            serial_numbers: row.serialized ? (row.serialNumbers || []) : []
        }));

        setLoading(true);
        try {
            const response = await api.post('/products/bulk_create', { products: productsData });
            if (response.success) {
                toast.success(`Successfully created ${response.data?.created_count || validRows.length} products!`);
                navigate('/products');
            } else {
                const errorMsg = response.errors
                    ? (Array.isArray(response.errors) ? response.errors[0] : JSON.stringify(response.errors))
                    : (response.message || response.error || 'Error creating products. Please try again.');
                toast.error(`Failed to save products: ${errorMsg}`);
            }
        } catch (error: any) {
            console.error('Error creating products:', error);
            toast.error(`Failed to save products: ${error.message || 'An unexpected error occurred.'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>, rowIndex: number, field: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const fields = ['name', 'productType', 'categoryId', 'sku', 'hsnCode', 'unit', 'tax_rate_id', 'stockQuantity', 'purchasePrice', 'mrp', 'sellingPrice', 'taxInclusive'];
            const fieldIndex = fields.indexOf(field);

            if (fieldIndex < fields.length - 1) {
                const inputs = document.querySelectorAll(`input[data-row="${rowIndex}"], select[data-row="${rowIndex}"]`);
                if (inputs[fieldIndex + 1]) (inputs[fieldIndex + 1] as HTMLElement).focus();
            } else {
                if (rowIndex === rows.length - 1) {
                    addRow();
                    setTimeout(() => {
                        const firstInputOfNewRow = document.querySelector(`input[data-row="${rowIndex + 1}"][data-field="name"]`);
                        if (firstInputOfNewRow) (firstInputOfNewRow as HTMLElement).focus();
                    }, 50);
                } else {
                    const nextRowInput = document.querySelector(`input[data-row="${rowIndex + 1}"][data-field="name"]`);
                    if (nextRowInput) (nextRowInput as HTMLElement).focus();
                }
            }
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-6rem)] bg-slate-50 rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            {/* Top Bar */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0 z-20 shadow-sm">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/products')} className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-50 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div>
                        <Text variant="h3" color="primary">Quick Product Entry</Text>
                        <Text variant="small" color="slate">Add multiple products efficiently</Text>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/products')} className="px-4 py-2 text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-semibold rounded-lg shadow-sm transition-all">
                        Cancel
                    </button>
                    <button
                        onClick={saveProducts}
                        disabled={loading}
                        className="px-6 py-2 bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:shadow-none"
                    >
                        {loading ? (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                        <span>Save Products</span>
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-auto bg-slate-50 p-6 relative w-full">
                <div className="min-w-[1500px] bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-100 sticky top-0 z-10">
                            <tr>
                                <th className="px-4 py-3 border-b border-slate-200 w-12 text-center"><Text variant="label" color="slate">#</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 min-w-[240px]">
                                    <div className="flex items-center gap-1">
                                        <Text variant="label" color="slate">Product Name</Text>
                                        <span className="text-red-500">*</span>
                                    </div>
                                </th>
                                <th className="px-4 py-3 border-b border-slate-200 w-36"><Text variant="label" color="slate">Classification</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-40"><Text variant="label" color="slate">Category</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-32"><Text variant="label" color="slate">SKU</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-24"><Text variant="label" color="slate">HSN</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-24"><Text variant="label" color="slate">Unit</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-24 text-right"><Text variant="label" color="slate">Tax</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-36 text-right"><Text variant="label" color="slate">Stock</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-32 text-right"><Text variant="label" color="slate">Cost Price</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-32 text-right"><Text variant="label" color="slate">MRP</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-32 text-right"><Text variant="label" color="slate">Sell Price</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-24 text-center"><Text variant="label" color="slate">Tax Inc?</Text></th>
                                <th className="px-4 py-3 border-b border-slate-200 w-12"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rows.map((row, index) => (
                                <tr key={row.id} className="group hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-3 text-center align-middle">
                                        <Text variant="mono" color="slate" className="text-xs">{index + 1}</Text>
                                    </td>
                                    <td className="px-2 py-2 p-0 relative align-top">
                                        <input
                                            type="text"
                                            data-row={index}
                                            data-field="name"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all placeholder-slate-400 text-sm font-black text-slate-900"
                                            placeholder="Enter product name..."
                                            value={row.name}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                updateRow(index, { name: val });
                                                searchProducts(val, index);
                                            }}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'name')}
                                            onFocus={() => { if (row.name && row.name.length >= 2) searchProducts(row.name, index); }}
                                            onBlur={() => setTimeout(() => updateRow(index, { showResults: false }), 200)}
                                            autoFocus={index === rows.length - 1 && rows.length > 1}
                                        />
                                        {row.showResults && searchCache[index] && searchCache[index].length > 0 && (
                                            <div className="absolute top-full left-2 right-2 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto mt-1 divide-y divide-slate-100 ring-1 ring-black ring-opacity-5">
                                                {searchCache[index].map(product => (
                                                    <div
                                                        key={product.id}
                                                        onClick={async () => {
                                                            try {
                                                                const res = await api.get(`/products/${product.id}`);
                                                                const fullProduct = res.data?.product || res.data;
                                                                updateRow(index, { 
                                                                    name: fullProduct.name, 
                                                                    sku: fullProduct.sku,
                                                                    hsnCode: fullProduct.hsn_code,
                                                                    unit: fullProduct.unit,
                                                                    tax_rate_id: fullProduct.tax_rate_id,
                                                                    tax_rate: fullProduct.tax_rate,
                                                                    taxInclusive: fullProduct.tax_inclusive,
                                                                    purchasePrice: fullProduct.purchase_price,
                                                                    mrp: fullProduct.mrp,
                                                                    sellingPrice: fullProduct.selling_price,
                                                                    categoryId: fullProduct.category_id,
                                                                    batches: fullProduct.batches || [],
                                                                    showResults: false 
                                                                });
                                                            } catch (e) {
                                                                console.error("Failed to fetch product details", e);
                                                                updateRow(index, { name: product.name, showResults: false });
                                                            }
                                                        }}
                                                        className="px-4 py-3 hover:bg-amber-50 cursor-pointer transition-colors"
                                                    >
                                                        <Text weight="semibold" color="primary">{product.name}</Text>
                                                        <Text variant="small" weight="medium" className="text-amber-600 block mt-0.5">Existing Product</Text>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <select
                                            data-row={index}
                                            data-field="productType"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm text-slate-700"
                                            value={row.productType}
                                            onChange={(e) => updateRow(index, { productType: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'productType')}
                                        >
                                            <option value="store_sale">Store Sale</option>
                                            <option value="electronics">Electronics</option>
                                            <option value="fashion">Fashion</option>
                                            <option value="shoes">Shoes</option>
                                            <option value="grocery">Grocery</option>
                                            <option value="pharmacy">Pharmacy</option>
                                            <option value="retail">Retail</option>
                                            <option value="rental">Rental</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <select
                                            data-row={index}
                                            data-field="categoryId"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm text-slate-700"
                                            value={row.categoryId}
                                            onChange={(e) => updateRow(index, { categoryId: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'categoryId')}
                                        >
                                            <option value="">Select...</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <input
                                            type="text"
                                            data-row={index}
                                            data-field="sku"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm font-mono text-slate-600"
                                            placeholder="SKU"
                                            value={row.sku}
                                            onChange={(e) => updateRow(index, { sku: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'sku')}
                                        />
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <input
                                            type="text"
                                            data-row={index}
                                            data-field="hsnCode"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm font-mono text-slate-600"
                                            placeholder="HSN"
                                            value={row.hsnCode}
                                            onChange={(e) => updateRow(index, { hsnCode: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'hsnCode')}
                                        />
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <select
                                            data-row={index}
                                            data-field="unit"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm text-slate-700"
                                            value={row.unit}
                                            onChange={(e) => updateRow(index, { unit: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'unit')}
                                        >
                                            <option value="piece">Piece</option>
                                            <option value="kg">Kg</option>
                                            <option value="gram">Gram</option>
                                            <option value="liter">Liter</option>
                                            <option value="meter">Meter</option>
                                            <option value="box">Box</option>
                                        </select>
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top text-right">
                                        <select
                                            data-row={index}
                                            data-field="tax_rate_id"
                                            className="w-full h-10 px-2 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-sm font-black text-emerald-900 text-right"
                                            value={row.tax_rate_id}
                                            onChange={(e) => {
                                                const selectedTax = taxRates.find(t => String(t.id) === e.target.value);
                                                updateRow(index, { 
                                                    tax_rate_id: e.target.value, 
                                                    tax_rate: selectedTax ? parseFloat(selectedTax.percentage) : 0 
                                                });
                                            }}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'tax_rate_id')}
                                        >
                                            <option value="">N/A</option>
                                            {taxRates.map(tax => (
                                                <option key={tax.id} value={tax.id}>{tax.name} ({tax.percentage}%)</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <div className="flex gap-2 items-start">
                                            <input
                                                type="number"
                                                data-row={index}
                                                data-field="stockQuantity"
                                                className="flex-1 h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm text-right font-black text-slate-900 font-mono"
                                                placeholder="0"
                                                value={row.stockQuantity}
                                                onChange={(e) => updateRow(index, { stockQuantity: e.target.value })}
                                                onKeyDown={(e) => handleKeyDown(e, index, 'stockQuantity')}
                                                disabled={row.serialized}
                                            />
                                            <button
                                                onClick={() => updateRow(index, {
                                                    serialized: !row.serialized,
                                                    stockQuantity: (!row.serialized && row.serialNumbers?.length) ? row.serialNumbers.length : row.stockQuantity
                                                })}
                                                className={`h-10 w-10 flex items-center justify-center rounded-lg border-2 transition-all ${row.serialized ? 'border-indigo-500 bg-indigo-50 text-indigo-600' : 'border-transparent bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                                                title="Toggle Serialized"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                                </svg>
                                            </button>
                                        </div>
                                        {row.serialized && (
                                            <div className="mt-1 px-1">
                                                <div
                                                    onClick={() => {
                                                        const serials = prompt("Enter Serial Numbers (comma separated)", row.serialNumbers ? row.serialNumbers.join(',') : '');
                                                        if (serials !== null) {
                                                            const validSerials = serials.split(',').map(s => s.trim()).filter(s => s);
                                                            updateRow(index, {
                                                                serialNumbers: validSerials,
                                                                stockQuantity: validSerials.length
                                                            });
                                                        }
                                                    }}
                                                    className="cursor-pointer hover:underline flex items-center gap-1"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                    </svg>
                                                    <Text variant="small" weight="medium" color="indigo">
                                                        {row.serialNumbers && row.serialNumbers.length > 0
                                                            ? `${row.serialNumbers.length} Serial(s)`
                                                            : "Add Serials"}
                                                    </Text>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <input
                                            type="number"
                                            data-row={index}
                                            data-field="purchasePrice"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm text-right font-black text-slate-900 font-mono"
                                            placeholder="0.00"
                                            value={row.purchasePrice}
                                            onChange={(e) => updateRow(index, { purchasePrice: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'purchasePrice')}
                                        />
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top">
                                        <input
                                            type="number"
                                            data-row={index}
                                            data-field="mrp"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm text-right font-black text-slate-900 font-mono"
                                            placeholder="0.00"
                                            value={row.mrp}
                                            onChange={(e) => updateRow(index, { mrp: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'mrp')}
                                        />
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top text-right">
                                        <input
                                            type="number"
                                            data-row={index}
                                            data-field="sellingPrice"
                                            className="w-full h-10 px-3 rounded-lg border-2 border-transparent bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm text-right font-black text-indigo-900 font-mono"
                                            placeholder="0.00"
                                            value={row.sellingPrice}
                                            onChange={(e) => updateRow(index, { sellingPrice: e.target.value })}
                                            onKeyDown={(e) => handleKeyDown(e, index, 'sellingPrice')}
                                        />
                                    </td>
                                    <td className="px-2 py-2 p-0 align-top text-center align-middle">
                                        <label className="relative inline-flex items-center cursor-pointer mt-2 w-full justify-center">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={row.taxInclusive}
                                                onChange={(e) => updateRow(index, { taxInclusive: e.target.checked })}
                                                data-row={index}
                                                data-field="taxInclusive"
                                                onKeyDown={(e) => handleKeyDown(e, index, 'taxInclusive')}
                                            />
                                            <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[calc(50%-16px)] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                                        </label>
                                    </td>
                                    <td className="px-2 py-2 text-center align-middle">
                                        <button
                                            onClick={() => removeRow(index)}
                                            className="text-slate-300 hover:text-red-500 hover:bg-red-50 p-2 rounded-lg transition-all"
                                            title="Remove Row"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div
                    onClick={addRow}
                    className="mt-4 border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-indigo-500 hover:bg-indigo-50/50 cursor-pointer transition-all flex items-center justify-center gap-2 group"
                >
                    <div className="h-8 w-8 rounded-full bg-slate-100 group-hover:bg-indigo-100 text-slate-500 group-hover:text-indigo-600 flex items-center justify-center transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                    </div>
                    <Text variant="body" weight="medium" color="slate" className="group-hover:text-indigo-600">Add New Line Item</Text>
                </div>
            </div>
        </div>
    );
}

export default QuickProductEntry;
